import * as React from 'react'
import { useGlobalState } from 'piral'
import { useLocation, matchPath } from 'react-router-dom'
import { setTelemetryView } from './faro'

/**
 * Name the current view for Faro on every route change.
 *
 * The point of this hook is **cardinality**. Faro's own `ViewInstrumentation` records
 * the URL, and a URL is a terrible dimension here: `/watch/movie/8412` and
 * `/watch/movie/9137` are the same page as far as "how slow is the watch page?" is
 * concerned, and this app additionally rewrites `?t=<seconds>` into the address bar
 * every few seconds while a stream plays. Grouped by URL, the watch page's vitals
 * scatter across thousands of one-sample series and no percentile means anything.
 *
 * So the view is the *route pattern* — `/watch/:media_type/:video_id` — resolved by
 * asking Piral which of its registered pages matches. That registry is the shell's
 * own source of truth and is populated by the pilets themselves, so the shell needs
 * no hard-coded list of routes and a new pilet page is named correctly the day it
 * ships, with nothing to update here.
 *
 * A path that matches no registration (the 404, or a page whose pilet has not loaded
 * yet) falls back to the raw pathname — never the query string, which
 * `stripQuery` in faro.ts also enforces on the way out.
 */
export function useViewTracking (): void {
  const { pathname } = useLocation()
  // `registry.pages` is keyed by the route pattern each pilet registered.
  const pages = useGlobalState(state => state.registry.pages)

  React.useEffect(() => {
    const patterns = Object.keys(pages)
    // Longest pattern first, so `/videos/category/:id` is tested before `/videos`
    // and a nested page is never reported under its parent's name. `exact` alone
    // does not settle this — two patterns can both match exactly at different
    // depths only if one is a prefix of the other, but ordering makes the
    // resolution deterministic rather than dependent on registration order.
    const matched = patterns
      .slice()
      .sort((a, b) => b.length - a.length)
      .find(pattern => matchPath(pathname, { path: pattern, exact: true }))

    setTelemetryView(matched ?? pathname)
  }, [pathname, pages])
}
